/**
 * Cura — About Banner Slider
 * -----------------------------------------------------------------
 * Infinite-loop transform-based slider.
 * - Clones first and last slide for seamless wrapping
 * - Pointer/touch drag support
 * - Autoplay with pause-on-hover and pause-on-focus
 * - Arrows + dots (hidden on mobile via CSS)
 * - Triggers content animations on each slide entry
 */
(function () {
  'use strict';

  var ANIM_DURATION = 550; // must match CSS transition duration (ms)

  class CuraAboutBanner {
    constructor(section) {
      this.section   = section;
      this.track     = section.querySelector('[data-ab-track]');
      this.slides    = Array.from(section.querySelectorAll('[data-ab-slide]'));
      this.prevBtn   = section.querySelector('[data-ab-prev]');
      this.nextBtn   = section.querySelector('[data-ab-next]');
      this.dotsWrap  = section.querySelector('[data-ab-dots]');
      this.dots      = this.dotsWrap ? Array.from(this.dotsWrap.querySelectorAll('[data-ab-dot]')) : [];

      this.count     = this.slides.length;
      this.current   = 0; // logical index (0-based, relative to originals)
      this.isAnim    = false;

      // Autoplay settings
      this.autoplay  = section.dataset.autoplay === 'true';
      this.interval  = parseInt(section.dataset.interval, 10) || 5000;
      this.timer     = null;

      if (this.count < 2) {
        // Single slide — just reveal content, no slider needed
        this.revealSlideContent(0);
        return;
      }

      this.buildClones();
      this.setPosition(this.current, false);
      this.bindEvents();
      this.revealSlideContent(0);

      if (this.autoplay) this.startAutoplay();
    }

    // ------------------------------------------------------------------ //
    // Clone first + last slides for seamless infinite loop
    // Track layout: [clone-of-last] [slide0] [slide1] … [slideN] [clone-of-first]
    // ------------------------------------------------------------------ //
    buildClones() {
      var first = this.slides[0].cloneNode(true);
      var last  = this.slides[this.count - 1].cloneNode(true);
      first.setAttribute('aria-hidden', 'true');
      last.setAttribute('aria-hidden', 'true');
      first.classList.remove('is-active');
      last.classList.remove('is-active');
      first.removeAttribute('data-ab-slide');
      last.removeAttribute('data-ab-slide');

      this.track.appendChild(first);
      this.track.insertBefore(last, this.track.firstChild);

      // Total items = originals + 2 clones; originals start at index 1
      this.cloneOffset = 1;
    }

    // Position track without transition (instant = true by default when false = animate)
    setPosition(logicalIndex, animate) {
      var realIndex = logicalIndex + this.cloneOffset;
      var offset = -realIndex * 100;

      if (animate === false) {
        this.track.classList.remove('is-animating');
      } else {
        this.track.classList.add('is-animating');
      }

      this.track.style.transform = 'translateX(' + offset + '%)';
    }

    // ------------------------------------------------------------------ //
    // Go to a logical slide index (with infinite-loop handling)
    // ------------------------------------------------------------------ //
    goTo(index, fromAutoplay) {
      if (this.isAnim) return;

      var prev = this.current;

      // Clamp to allow going one past each end (for the clone jump)
      if (index < 0)           index = this.count - 1;
      if (index >= this.count) index = 0;

      if (index === prev && !fromAutoplay) return;

      this.isAnim = true;

      // Deactivate old slide content
      this.slides[prev].classList.remove('is-active');

      // Determine real (clone-aware) direction
      var realTarget = index + this.cloneOffset;
      this.track.classList.add('is-animating');
      this.track.style.transform = 'translateX(' + (-realTarget * 100) + '%)';

      // After transition: if we landed on a clone, instantly jump to the real slide
      setTimeout(() => {
        this.track.classList.remove('is-animating');

        // Jump from after-last clone → first real
        // Jump from before-first clone → last real
        // (Already handled by index clamping above, but ensure position matches)
        this.current = index;
        this.track.style.transform = 'translateX(' + (-(index + this.cloneOffset) * 100) + '%)';

        this.slides[index].classList.add('is-active');
        this.updateDots(index);
        this.revealSlideContent(index);
        this.isAnim = false;
      }, ANIM_DURATION);
    }

    // Animate: go past the clone, then silently jump
    goToWithCloneJump(direction) {
      if (this.isAnim) return;
      this.isAnim = true;

      var prev = this.current;
      var cloneRealIndex; // real index of the clone we're animating into

      if (direction === 1) {
        // Moving forward: animate into clone-of-first (at end)
        cloneRealIndex = this.count + this.cloneOffset;
      } else {
        // Moving backward: animate into clone-of-last (at start = index 0)
        cloneRealIndex = 0;
      }

      this.slides[prev].classList.remove('is-active');
      this.track.classList.add('is-animating');
      this.track.style.transform = 'translateX(' + (-cloneRealIndex * 100) + '%)';

      setTimeout(() => {
        // Silent jump to real counterpart
        var realTarget = direction === 1 ? 0 : this.count - 1;
        this.track.classList.remove('is-animating');
        this.track.style.transform = 'translateX(' + (-(realTarget + this.cloneOffset) * 100) + '%)';
        this.current = realTarget;
        this.slides[realTarget].classList.add('is-active');
        this.updateDots(realTarget);
        this.revealSlideContent(realTarget);
        this.isAnim = false;
      }, ANIM_DURATION);
    }

    next() {
      if (this.current === this.count - 1) {
        this.goToWithCloneJump(1);
      } else {
        this.goTo(this.current + 1);
      }
    }

    prev() {
      if (this.current === 0) {
        this.goToWithCloneJump(-1);
      } else {
        this.goTo(this.current - 1);
      }
    }

    // ------------------------------------------------------------------ //
    // Content animation: reset hidden state then let CSS transition in
    // ------------------------------------------------------------------ //
    revealSlideContent(index) {
      var slide = this.slides[index];
      if (!slide) return;

      // First reset all animated elements in this slide so they re-animate
      var animated = slide.querySelectorAll('[data-animate]');
      animated.forEach(function (el) {
        // Remove is-visible briefly so CSS transition re-fires
        el.classList.remove('is-visible');
        // Apply the delay from data-animate-delay as CSS var
        var delay = el.getAttribute('data-animate-delay');
        if (delay) el.style.setProperty('--animate-delay', delay + 'ms');
      });

      // Tiny timeout so the browser registers the class removal before re-adding
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          animated.forEach(function (el) {
            el.classList.add('is-visible');
          });
        });
      });
    }

    // ------------------------------------------------------------------ //
    // Dots
    // ------------------------------------------------------------------ //
    updateDots(index) {
      this.dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    // ------------------------------------------------------------------ //
    // Autoplay
    // ------------------------------------------------------------------ //
    startAutoplay() {
      this.stopAutoplay();
      this.timer = setInterval(() => {
        this.next();
      }, this.interval);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    // ------------------------------------------------------------------ //
    // Event binding
    // ------------------------------------------------------------------ //
    bindEvents() {
      // Arrows
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.stopAutoplay(); this.prev(); if (this.autoplay) this.startAutoplay(); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.stopAutoplay(); this.next(); if (this.autoplay) this.startAutoplay(); });

      // Dots
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          var idx = parseInt(dot.dataset.abDot, 10);
          this.stopAutoplay();
          this.goTo(idx);
          if (this.autoplay) this.startAutoplay();
        });
      });

      // Pause autoplay on hover / focus
      if (this.autoplay) {
        this.section.addEventListener('mouseenter', () => this.stopAutoplay());
        this.section.addEventListener('mouseleave', () => this.startAutoplay());
        this.section.addEventListener('focusin',    () => this.stopAutoplay());
        this.section.addEventListener('focusout',   () => this.startAutoplay());
      }

      // Keyboard navigation
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { this.stopAutoplay(); this.prev(); if (this.autoplay) this.startAutoplay(); }
        if (e.key === 'ArrowRight') { this.stopAutoplay(); this.next(); if (this.autoplay) this.startAutoplay(); }
      });

      // Touch / pointer drag
      this.bindDrag();
    }

    bindDrag() {
      var startX = 0;
      var startY = 0;
      var isDragging = false;
      var swiped = false;
      var THRESHOLD = 50; // px

      var onStart = (e) => {
        var point = e.touches ? e.touches[0] : e;
        startX = point.clientX;
        startY = point.clientY;
        isDragging = true;
        swiped = false;
        this.stopAutoplay();
      };

      var onEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;
        var point = e.changedTouches ? e.changedTouches[0] : e;
        var dx = point.clientX - startX;
        var dy = point.clientY - startY;
        // Only swipe if horizontal movement dominates
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > THRESHOLD) {
          swiped = true;
          if (dx < 0) { this.next(); } else { this.prev(); }
        }
        if (this.autoplay) this.startAutoplay();
      };

      this.track.addEventListener('touchstart', onStart, { passive: true });
      this.track.addEventListener('touchend',   onEnd,   { passive: true });
      this.track.addEventListener('pointerdown', onStart);
      this.track.addEventListener('pointerup',   onEnd);

      // Don't follow the whole-slide link if the gesture was a swipe
      this.track.addEventListener('click', function (e) {
        if (swiped && e.target.closest('[data-ab-slide-link]')) {
          e.preventDefault();
          e.stopPropagation();
          swiped = false;
        }
      }, true);
    }
  }

  // ------------------------------------------------------------------ //
  // Init
  // ------------------------------------------------------------------ //
  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-cura-ab]').forEach(function (el) {
      if (!el.dataset.abInit) {
        new CuraAboutBanner(el);
        el.dataset.abInit = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:reorder', function () { init(document); });
})();